import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { BitbucketTab } from '@atlassian/product-search-dialog';

import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { urls } from 'src/urls/dashboard';

import { PageContext } from '../../types';
import url, { isCSPUrl } from '../../urls';

import { messages } from './horizontal-platform-search.i18n';

const cspLinkContentStyles = xcss({
  paddingLeft: 'space.200',
});

export type CodeSearchLinkComponentProps = {
  href: string;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
};

/**
 * CodeSearchLinkComponent wraps the WorkspaceLinkComponent to add custom styling
 * and behavior for code search portal links. When the href is a CSP URL, it renders
 * custom content with an icon and label, and opens in a new tab.
 */
export const createCodeSearchLinkComponent = (
  WorkspaceLinkComponent: React.ComponentType<any>,
  formatMessage: IntlShape['formatMessage'],
  currentContext: PageContext,
  onSearchDrawerClose?: () => void
) => {
  return (linkComponentProps: CodeSearchLinkComponentProps) => {
    const onClickHandler = (e: React.MouseEvent) => {
      linkComponentProps.onClick?.(e);
      onSearchDrawerClose?.();
    };

    if (isCSPUrl(linkComponentProps.href)) {
      const repoName = currentContext.repository?.name;

      return (
        <a
          {...linkComponentProps}
          href={linkComponentProps.href}
          target="_blank"
          onClick={onClickHandler}
        >
          <Inline>
            <Box>
              <SearchIcon label="" />
            </Box>
            <Box xcss={cspLinkContentStyles}>
              {repoName
                ? formatMessage(messages.codeSearchPortalRepositoryLink, {
                    repoName,
                  })
                : formatMessage(messages.codeSearchPortalLink)}
            </Box>
          </Inline>
        </a>
      );
    }

    return (
      <WorkspaceLinkComponent
        {...linkComponentProps}
        onClick={onClickHandler}
      />
    );
  };
};

export const EMPTY_SEARCH_ITEMS = {
  size: 0,
  sections: [],
};

/**
 * Maps Bitbucket repositories to search items format
 */
export const mapBitbucketReposToSearchItems = (
  title: string,
  repos: BB.Repository[]
) => {
  if (!repos.length) {
    // If we've tried loading repos and still have no results, return the
    // empty placeholder
    return EMPTY_SEARCH_ITEMS;
  }

  return {
    size: repos.length,
    sections: [
      {
        id: 'bitbucket.repository',
        title,
        searchResults: repos.map((repo: BB.Repository) => {
          return {
            title: repo.name,
            id: repo.uuid,
            // The blank meta field removes the workspace name from the search result.
            meta: '',
            url: repo.links.html.href,
            iconUrl: repo.links.avatar.href,
          };
        }),
      },
    ],
  };
};

/**
 * Pre-query item supplier for repositories
 * XXX This search component seems to hold onto the very first values from the
 * ReposContext and not get updated. The effect of that is that we may have
 * already loaded repositories on the dashboard, but this component won't see
 * them. The current state is that we always show the empty placeholder, but
 * these changes mean we'll make a request for the recent repos. Even though
 * that's a duplicate request on the dashboard page, it's an improvement on all
 * other pages.
 */
export const preQueryItemSupplierOverride = async (
  repositories: BB.Repository[],
  loadRepositories: (includeStatus: boolean) => Promise<BB.Repository[]>,
  title: string
) => {
  let repos = repositories;
  if (!repos.length) {
    repos = await loadRepositories(false);
  }

  return mapBitbucketReposToSearchItems(title, repos);
};

/**
 * Post-query item supplier for repositories
 * This override ensures that the fetch ahead queries are workspace scoped queries.
 * It also removes the workspace from the meta attribute from the search item.
 * This is part of the effort to deprecate cross-workspace requests.
 */
export const postQueryItemSupplierOverride = async (
  query: string,
  workspaceSlug: string,
  title: string
) => {
  const response = await fetch(
    urls.api.v20.profileRepositories(workspaceSlug, { q: `name ~ "${query}"` })
  );

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  const repositoryData = await response.json();
  const repos = repositoryData.values;

  return mapBitbucketReposToSearchItems(title, repos);
};

/**
 * Generates URL for the 'Advanced Search' link in the 'no results' view.
 */
export const urlGeneratorForNoResultsScreenOverride = (
  isCodeSearchPortalLinkEnabled: boolean,
  workspaceSlug: string,
  query?: string
) => {
  if (isCodeSearchPortalLinkEnabled) {
    return url.external.codeSearchPortal(query);
  }
  return url.ui.workspaceSearch(workspaceSlug, query);
};

/**
 * Generates URL for code search when no context object is provided.
 */
export const codeSearchUrlGeneratorOverride = (
  isCodeSearchPortalLinkEnabled: boolean,
  workspaceSlug: string,
  query?: string
) => {
  if (isCodeSearchPortalLinkEnabled) {
    return url.external.codeSearchPortal(query);
  }
  return url.ui.workspaceSearch(workspaceSlug, query);
};

/**
 * Generates URL for contextual code search; used when a workspace/repository context is provided.
 */
export const codeSearchContextualUrlGeneratorOverride = (
  isCodeSearchPortalLinkEnabled: boolean,
  context: PageContext,
  workspaceSlug: string,
  query?: string
) => {
  const { repository } = context;
  const modifierParts = [
    repository ? `repo:${repository.slug}` : '',
    query?.trim() ?? '',
  ].filter(Boolean);
  const fullModifier = modifierParts.join(' ');

  if (isCodeSearchPortalLinkEnabled) {
    return url.external.codeSearchPortal(fullModifier || undefined);
  }

  return url.ui.workspaceSearch(
    workspaceSlug,
    encodeURIComponent(fullModifier)
  );
};

type BitbucketTabComponentProps = {
  onRetry: () => void;
  repositories: BB.Repository[];
  loadRepositories: (includeStatus: boolean) => Promise<BB.Repository[]>;
  workspaceSlug: string;
  currentContext: PageContext;
  workspaceLinkComponent?: React.ComponentType;
  onSearchDrawerClose?: () => void;
};

/**
 * BitbucketTabWrapper renders the platform's BitbucketTab with overrides
 * for URL generation and repo listing.
 */
export const BitbucketTabWrapper = ({
  onRetry,
  repositories,
  loadRepositories,
  workspaceSlug,
  currentContext,
  workspaceLinkComponent,
  onSearchDrawerClose,
}: BitbucketTabComponentProps) => {
  const intl = useIntl();

  const isCodeSearchPortalLinkEnabled = useStatsigGate(
    StatsigFeatureKeys.codeSearchPortalLink
  );

  const linkComponent = workspaceLinkComponent
    ? createCodeSearchLinkComponent(
        workspaceLinkComponent,
        intl.formatMessage,
        currentContext,
        onSearchDrawerClose
      )
    : undefined;
  return (
    <BitbucketTab
      onRetry={onRetry}
      order={0}
      // TODO: use recent repos API for preQueryItemSupplier when recent repos service will be done
      preQueryItemSupplier={() =>
        preQueryItemSupplierOverride(
          repositories,
          loadRepositories,
          intl.formatMessage(messages.repositoriesHeading)
        )
      }
      postQueryItemSupplier={({ query }) =>
        postQueryItemSupplierOverride(
          query,
          workspaceSlug,
          intl.formatMessage(messages.repositoriesHeading)
        )
      }
      isPrefetchingEnabled={false}
      viewAllLinkGenerator={(search?: string) =>
        url.ui.repositories(workspaceSlug, search)
      }
      urlGeneratorForNoResultsScreen={(query?: string) =>
        urlGeneratorForNoResultsScreenOverride(
          isCodeSearchPortalLinkEnabled,
          workspaceSlug,
          query
        )
      }
      codeSearchUrlGenerator={(query?: string) =>
        codeSearchUrlGeneratorOverride(
          isCodeSearchPortalLinkEnabled,
          workspaceSlug,
          query
        )
      }
      codeSearchContextualUrlGenerator={(query?: string) =>
        codeSearchContextualUrlGeneratorOverride(
          isCodeSearchPortalLinkEnabled,
          currentContext,
          workspaceSlug,
          query
        )
      }
      permissionSupplier={() => Promise.resolve(['bitbucket.repository'])}
      currentContext={currentContext}
      linkComponent={linkComponent}
    />
  );
};
